import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../settings';

function VolunteerList() {
  const { user } = useAuth();
  const [volunteers, setVolunteers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    id: '',
    username: '',
    password: '',
    name: '',
    active: true
  });

  const fetchVolunteers = async () => {
    try {
      let url = `${API_URL}/users?role=VOLUNTEER`;
      if (user.hospital_id) {
        url += `&hospital_id=${user.hospital_id}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      setVolunteers(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchVolunteers();
    // eslint-disable-next-line
  }, [user]);

  const handleOpenAdd = () => {
    setFormData({
      id: '',
      username: '',
      password: '',
      name: '',
      active: true
    });
    setEditMode(false);
    setError('');
    setOpen(true);
  };

  const handleOpenEdit = (vol) => {
    setFormData({
      id: vol.id,
      username: vol.username,
      password: vol.password,
      name: vol.name,
      active: vol.active !== false
    });
    setEditMode(true);
    setError('');
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.username || !formData.password || !formData.name) {
      setError('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    try {
      const payload = {
        username: formData.username,
        password: formData.password,
        name: formData.name,
        active: formData.active,
        role: "VOLUNTEER",
        organization: "อาสาสมัครสาธารณสุขประจำหมู่บ้าน",
        hospital_id: user.hospital_id || 'HSP01' // Fallback for ADMIN without hospital_id
      };

      if (editMode) {
        await fetch(`${API_URL}/users/${formData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        // Check if username exists
        const checkRes = await fetch(`${API_URL}/users?username=${formData.username}`);
        const exist = await checkRes.json();
        if (exist.length > 0) {
          setError('ชื่อผู้ใช้นี้มีในระบบแล้ว');
          return;
        }

        // json-server generates an id automatically
        payload.id = `VOL${Math.floor(Math.random() * 10000)}`;

        await fetch(`${API_URL}/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      
      setOpen(false);
      fetchVolunteers();
    } catch (err) {
      setError('เกิดข้อผิดพลาด: ' + err.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          จัดการข้อมูล อสม.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          เพิ่ม อสม.
        </Button>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f4f6f8' }}>
            <TableRow>
              <TableCell><strong>ชื่อ-นามสกุล</strong></TableCell>
              <TableCell><strong>ชื่อผู้ใช้ (Username)</strong></TableCell>
              <TableCell><strong>สถานะ</strong></TableCell>
              <TableCell align="center"><strong>จัดการ</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {volunteers.map((vol) => (
              <TableRow key={vol.id}>
                <TableCell>{vol.name}</TableCell>
                <TableCell>{vol.username}</TableCell>
                <TableCell>
                  {vol.active !== false ? (
                    <Chip label="เปิดใช้งาน" color="success" size="small" />
                  ) : (
                    <Chip label="ระงับ" color="error" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Button 
                    variant="outlined" 
                    size="small" 
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenEdit(vol)}
                  >
                    แก้ไข
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {volunteers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">ไม่มีข้อมูล อสม.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog for Add/Edit */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'แก้ไขข้อมูล อสม.' : 'เพิ่มข้อมูล อสม.'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <TextField
            fullWidth
            margin="normal"
            label="ชื่อ-นามสกุล"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="ชื่อผู้ใช้ (Username)"
            name="username"
            value={formData.username}
            onChange={handleChange}
            disabled={editMode} // Disable editing username for safety
          />
          <TextField
            fullWidth
            margin="normal"
            label="รหัสผ่าน"
            name="password"
            value={formData.password}
            onChange={handleChange}
          />
          
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleChange}
                  name="active"
                  color="primary"
                />
              }
              label={formData.active ? "สถานะ: เปิดใช้งาน" : "สถานะ: ระงับการใช้งาน"}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>ยกเลิก</Button>
          <Button onClick={handleSubmit} variant="contained">
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VolunteerList;